/*
 *
 * ChannelsPage reducer
 *
 */

import {
  SET_CLIENT_FILTER,
  SET_STATUS_FILTER,
  CLEAR_SAVE_ERROR,
  FETCH_CHANNELS_SUCCESS,
  FETCH_CHANNELS_REQUEST,
  POST_CHANNEL_REQUEST,
  PUT_CHANNEL_SUCCESS,
  FETCH_CLIENTS_SUCCESS,
  PATCH_CHANNEL_REQUEST,
  PATCH_CHANNEL_SUCCESS,
  CHANGE_TAB,
  FETCH_CHANNELS_FAILURE,
  PUT_CHANNEL_FAILURE,
  FETCH_THEMES_SUCCESS,
  FETCH_MODES_REQUEST,
  FETCH_MODES_SUCCESS,
  FETCH_MODES_FAILURE,
  DELETE_CHANNEL_SUCCESS,
  ARCHIVE_CHANNEL_SUCCESS,
  FETCH_CHANNEL_DETAIL_REQUEST,
  FETCH_UPDATED_CHANNEL_DETAIL_REQUEST,
  FETCH_CHANNEL_DETAIL_SUCCESS,
  FETCH_CHANNEL_DETAIL_FAILURE,
  FETCH_SLUG_VALIDATE_REQUEST,
  FETCH_SLUG_VALIDATE_SUCCESS,
  FETCH_SLUG_VALIDATE_FAILURE,
  SET_CHANEL_FORM,
} from './constants';
import { toNumber } from 'utils/helpers'

const initialState = {
  clientFilter: "",
  statusFilter: "active",
  allChannels: null,
  channelDetail: null,
  channelDetailError: null,
  // The editor's photo_filters field holds raw File objects until a refresh
  // lands and re-seeds it from server truth. See the ordering matrix above
  // `isCurrentDetailResponse` for what each of these means and why there are
  // three of them rather than one.
  refreshPending: false,
  detailIssuedId: 0,
  detailSavedAtId: 0,
  isFetching: false,
  // Whether the channel list request has SETTLED, either way. `isFetching`
  // cannot answer that: it is false both before the first request is
  // dispatched and after one comes back, so on its own it leaves the initial
  // "no channels yet" render indistinguishable from "the server says you have
  // none". Set on both success and failure — a failed fetch has settled too.
  loaded: false,
  // Why the last list fetch failed, or null. Kept OFF the shared `errors` for
  // the same reason `channelDetailError` is: `errors` is written by
  // PUT_CHANNEL_FAILURE too, and Forms/index.js toasts on it, so a failed SAVE
  // would put the channel LIST into its error state. The two are different
  // events about different requests and only the list one may govern what the
  // list renders.
  //
  // Cleared by FETCH_CHANNELS_SUCCESS and nothing else — deliberately NOT by
  // FETCH_CHANNELS_REQUEST. Clearing on dispatch would drop the page back to
  // the empty state for the length of every retry, which is the exact
  // "you have nothing" lie this field exists to stop; `isFetching` is what the
  // error state reads to show the retry in progress instead.
  listError: null,
  clients: {},
  currentTab: 0,
  channelThemes: {},
  modes: {},
  channelThemeInstances: {},
  paging: {},
  errors: null,
  isSaving: false,
  validSlug: true,
  slugIsValidating: false,
  // True when the availability probe itself failed (network, timeout, 5xx), as
  // opposed to the server answering "taken". Save stays blocked either way, but
  // the form must not tell the user a link is taken when we simply don't know.
  slugCheckFailed: false,
  // The server's rejection of the last create/edit save, or null. Kept apart
  // from `errors` (also written by list fetches) so the wizard shows it inline
  // beside Save and nothing else can put a list failure there.
  saveError: null,
  // True when the capture-mode list failed to load, so the Photos tab can say
  // so and offer a retry instead of a Save that can never enable.
  modesError: false,
  channelForm: {}
};

// ---------------------------------------------------------------------------
// The detail-response ordering matrix.
//
// Detail requests are issued from three places that do not coordinate: the
// editor's mount fetch, its post-save refresh (which Retry re-issues), and
// LeadsPage's own mount fetch. `takeLatest` cancels within a watcher, never
// across them, so several detail requests can be in flight at once and their
// responses can arrive in any order. Two INDEPENDENT questions have to be
// answered about every arrival, and conflating them is what produced a run of
// narrower and narrower ordering bugs:
//
//   Q1  Does this response describe the freshest server state?
//       -> Only if it answers the LATEST ISSUED request (`detailIssuedId`).
//          Comparing against the latest SETTLED request instead accepts any
//          response whenever the newer request is merely still in flight.
//
//   Q2  Does this response resolve the post-save refresh?
//       -> Only if its request was ISSUED AFTER the save (`detailSavedAtId`,
//          the issued-watermark captured at PATCH_CHANNEL_SUCCESS). A request
//          issued BEFORE the save returns PRE-SAVE data; letting it resolve
//          the refresh both re-opens the save path and feeds pre-save filters
//          to the re-seed, which is worse than not refreshing at all.
//
// Q1 does not imply Q2. A response can be the latest issued and still predate
// the save — the save completes, and the post-save refresh is dispatched from
// a React effect, so an already-in-flight response can land in between.
//
// The full matrix, one row per (outcome, id-vs-issued, id-vs-save):
//
//   #   save outstanding | kind | vs issued  | vs save    | effect
//   1   no               | ok   | current    | -          | apply detail, clear error
//   2   no               | err  | current    | -          | set error
//   3   no               | ok   | superseded | -          | ignore entirely
//   4   no               | err  | superseded | -          | ignore entirely
//   5   yes              | ok   | current    | after save | apply detail, clear error,
//                                                           CLEAR refreshPending -> re-seed
//   6   yes              | ok   | current    | before save| apply detail, clear error,
//                                                           KEEP refreshPending -> no re-seed
//   7   yes              | err  | current    | after save | set error, keep refreshPending
//   8   yes              | err  | current    | before save| set error, keep refreshPending
//   9   yes              | ok   | superseded | either     | ignore entirely
//  10   yes              | err  | superseded | either     | ignore entirely
//  11   yes              | (a retry is issued, no response yet)  | keep refreshPending + error
//
// Row 6 is the one every previous round got wrong. Rows 3/4/9/10 are the ones
// a settled-based comparison got wrong whenever the newer request had not yet
// come back.
//
// Escapability follows from row 5 being reachable at all times: Retry issues a
// request whose id exceeds the watermark, so its success always resolves the
// refresh. A page reload starts from the initial state, where nothing is
// outstanding.
// ---------------------------------------------------------------------------

// Q1. A response with no id (a dispatcher predating request ids) is treated as
// current rather than silently dropped — it cannot be reasoned about, and
// dropping it would lose data.
const isCurrentDetailResponse = (state, action) =>
  typeof action.requestId !== "number" ||
  action.requestId === state.detailIssuedId;

// Q2. Fail closed: an id-less response cannot prove it postdates the save, so
// it never resolves the refresh.
const resolvesPostSaveRefresh = (state, action) =>
  typeof action.requestId === "number" &&
  action.requestId > state.detailSavedAtId;

// Ids come from one monotonic counter, so a request always advances the
// watermark; `max` only guards against an out-of-order or id-less dispatch
// walking it backwards, which would un-supersede older responses.
const nextIssuedId = (state, action) =>
  typeof action.requestId === "number"
    ? Math.max(state.detailIssuedId, action.requestId)
    : state.detailIssuedId;

function channelsPageReducer(state = initialState, action) {
  switch (action.type) {
    case SET_CLIENT_FILTER:
      return {
        ...state,
        clientFilter: action.payload,
      };
    case SET_STATUS_FILTER:
      return {
        ...state,
        statusFilter: action.payload,
      };
    case PATCH_CHANNEL_REQUEST:
      return {
        ...state,
        isSaving: true,
        saveError: null,
      };
    case FETCH_SLUG_VALIDATE_REQUEST:
      return {
        ...state,
        slugIsValidating: true,
        slugCheckFailed: false,
      };
    // The probe answers { slug, available }. Read `available` strictly: an
    // unexpected or empty body must never unblock Save (fail closed), which is
    // also why FETCH_SLUG_VALIDATE_FAILURE below sets validSlug: false.
    case FETCH_SLUG_VALIDATE_SUCCESS:
      return {
        ...state,
        slugIsValidating: false,
        slugCheckFailed: false,
        validSlug: action.payload?.available === true,
      };
    case FETCH_SLUG_VALIDATE_FAILURE:
        return {
          ...state,
          isFetching: false,
          slugIsValidating: false,
          slugCheckFailed: true,
          validSlug: false,
        };
    // A new form session must not inherit the previous form's rejection.
    case CLEAR_SAVE_ERROR:
      return { ...state, saveError: null };
    case POST_CHANNEL_REQUEST:
      return {
        ...state,
        isFetching: true,
        // Blocks a second Save while this create is in flight (see Forms).
        isSaving: true,
        saveError: null,
      };
    case FETCH_CHANNELS_REQUEST:
      return {
        ...state,
        isFetching: true,
      };
    // Matrix row 11. A request in flight settles nothing — neither the error
    // nor `refreshPending` clears here. Clearing on dispatch is what would
    // re-open the save path for the duration of a retry, which is the whole
    // window the guard exists to close. Issuing only advances the watermark,
    // which is what makes every earlier in-flight response superseded.
    // The updated (post-save) fetch keeps the existing channelDetail on
    // screen; the initial fetch clears it because the editor is loading a
    // channel from scratch.
    case FETCH_UPDATED_CHANNEL_DETAIL_REQUEST:
      return {
        ...state,
        detailIssuedId: nextIssuedId(state, action),
        isFetching: true,
      };
    case FETCH_CHANNEL_DETAIL_REQUEST:
      return {
        ...state,
        channelDetail: null,
        detailIssuedId: nextIssuedId(state, action),
        isFetching: true,
      };
    case FETCH_CHANNELS_SUCCESS:
      const { payload } = action;
      return {
        ...state,
        isFetching: false,
        loaded: true,
        allChannels: payload.data,
        paging: {
          currentPage: toNumber(payload.current_page),
          rowsPerPage: toNumber(payload.rows_per_page),
          pageCount: toNumber(payload.page_count),
        },
        errors: null,
        listError: null,
      }
    // Matrix rows 1, 5, 6, 9. A superseded response is dropped whole — it
    // describes a channel state a newer request has already replaced, so
    // letting it write would resurrect stale data or clear a live failure.
    case FETCH_CHANNEL_DETAIL_SUCCESS:
      if (!isCurrentDetailResponse(state, action)) return state;
      return {
        ...state,
        isFetching: false,
        channelDetail: action.payload,
        channelDetailError: null,
        // Row 5 vs row 6. Fresh server truth only counts as the post-save
        // refresh if this request went out AFTER the save; a response issued
        // before it carries pre-save filters, which must never reach the
        // re-seed.
        refreshPending: resolvesPostSaveRefresh(state, action)
          ? false
          : state.refreshPending,
      }
    // Matrix rows 2, 7, 8, 10. A failed detail fetch leaves the previous
    // channelDetail in place — the editor keeps rendering the channel it
    // already has. That is exactly why this error has to be recorded: without
    // it the failure is invisible, and the editor cannot tell "refreshed" from
    // "stale after a failed refresh". It never touches `refreshPending`: a
    // failure cannot resolve a refresh, only leave one outstanding — which is
    // why rows 7 and 8 have the same effect.
    case FETCH_CHANNEL_DETAIL_FAILURE:
      if (!isCurrentDetailResponse(state, action)) return state;
      return {
        ...state,
        isFetching: false,
        channelDetailError:
          action.payload?.message || "Unable to load this channel.",
      }
    // A save landed, so the editor's photo_filters now holds raw Files the
    // server has already stored. Until a refresh brings back server truth to
    // re-seed them, saving again would re-upload those Files and duplicate the
    // channel's filters — so the refresh is outstanding from this moment.
    //
    // Capturing the issued-watermark here is what makes row 6 distinguishable:
    // every request already in flight has an id <= this, so none of them can
    // resolve the refresh they predate. Only PATCH_CHANNEL_SUCCESS arms the
    // guard; a FAILED save stored nothing, so there is nothing stale to guard.
    case PATCH_CHANNEL_SUCCESS:
      return {
        ...state,
        isSaving: false,
        saveError: null,
        refreshPending: true,
        detailSavedAtId: state.detailIssuedId,
      }
    case PUT_CHANNEL_SUCCESS:
    case FETCH_CLIENTS_SUCCESS:
      return {
        ...state,
        isFetching: false,
        saveError: action.type === PUT_CHANNEL_SUCCESS ? null : state.saveError,
        isSaving: action.type === PUT_CHANNEL_SUCCESS ? false : state.isSaving,
        clients: {
          ...state.clients,
          ...action.payload.clients,
        },
        channelThemeInstances: {
          ...state.allChannels,
          ...action.payload.channelThemeInstances,
        },
        errors: null,
      };
    case FETCH_THEMES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        channelThemes: {
          ...action.payload.channelThemes,
        },
      };
    case FETCH_MODES_REQUEST:
      return {
        ...state,
        modesError: false,
      };
    case FETCH_MODES_FAILURE:
      return {
        ...state,
        isFetching: false,
        modesError: true,
      };
    case FETCH_MODES_SUCCESS:
      return {
        ...state,
        isFetching: false,
        modesError: false,
        modes: {
          ...action.payload.modes,
        },
      };
    // Split from PUT_CHANNEL_FAILURE to carry `loaded` and `listError`: a
    // failed LIST fetch has still settled, so the list must stop showing the
    // spinner — and it must say so, rather than falling through to "It's
    // looking empty around here…" about channels the user still owns. A failed
    // SAVE says nothing about whether the list ever loaded — and it can happen
    // before it did, on a deep link straight into the editor — so it writes
    // neither.
    case FETCH_CHANNELS_FAILURE:
      return {
        ...state,
        isFetching: false,
        loaded: true,
        errors: action.payload?.errors || "Something went wrong",
        listError: action.payload?.errors || "Something went wrong",
      };
    case PUT_CHANNEL_FAILURE:
      return {
        ...state,
        saveError: action.payload?.errors || {},
        isFetching: false,
        errors: action.payload?.errors || "Something went wrong",
        isSaving: false,
      };
    case CHANGE_TAB:
      return {
        ...state,
        currentTab: action.payload !== undefined ? action.payload : state.currentTab + 1,
      };
    case DELETE_CHANNEL_SUCCESS:
    case ARCHIVE_CHANNEL_SUCCESS:
      // Remove the affected row in place. Delete is terminal; archive moves the
      // channel out of the default `active` view (it reappears under the
      // `archived` status filter on the next fetch). Guard against a null list
      // so an out-of-order success can't throw.
      return {
        ...state,
        allChannels: (state.allChannels || []).filter(item => item.id !== action.channelId),
      };
    case SET_CHANEL_FORM:
      return {
        ...state,
        channelForm: {
          ...state.channelForm,
          ...action.payload,
        },
      };
    default:
      return state;
  }
}

export default channelsPageReducer;
